import mori from 'mori';
import { appDefault } from '../../config/config';
/**
 * Label the footnotes with numbers weighted by reused, row, and cell
 * @param  {mori.hashmap} footnotes [composed footnotes to access]
 * @param  {mori.hashmap} rows      [relevant rows to use]
 * @param  {mori.vector} headers    [headers]
 * @param  {mori.vector} timeframes [active timeframes]
 * @param  {mori.vector} baseContext [current geography context (Location, States, etc)]
 * @param  {bool} isGeoMulti whether the indicator data is geo-multi
 * @return {mori.hashmap}           [labeled footnotes]
 */
export default function labelFootnotes(
  footnotes,
  rows,
  headers,
  timeframes,
  baseContext,
  isGeoMulti
) {
  // This is an object map of note hashes to labels
  const usedFootnotes = {};

  const isTrendGraph = mori.count(timeframes) > 2;

  const getFootnoteSymbol = (index) => {
    const { footnoteSymbols } = appDefault;
    const instances = Math.ceil((index + 1) / footnoteSymbols.length);
    const newIndex = index - ((instances - 1) * footnoteSymbols.length);
    return {
      label: footnoteSymbols[newIndex].repeat(instances),
      weight: index + instances,
    };
  };

  // Push a footnote into usedFootnotes
  const pushFootnote = (hash) => {
    const notesCount = Object.keys(usedFootnotes).length;
    usedFootnotes[hash] = isGeoMulti
      ? { ...getFootnoteSymbol(notesCount) }
      : { label: notesCount + 1, weight: notesCount };
    return usedFootnotes[hash];
  };

  // Returns the label and symbol weight, if applicable
  // { label: 'int|str', weight: int }
  const getLabel = (noteHash) => ({
    ...(
      usedFootnotes[noteHash] ?
        usedFootnotes[noteHash] :
        pushFootnote(noteHash)
    ),
  });

  // Returns the label
  // const getLabel = (noteHash) => usedFootnotes[noteHash] ?
  //   usedFootnotes[noteHash] :
  //   pushFootnote(noteHash);

  const labeledFootnotes = mori.map(
    (footnote) => {
      const notes = mori.toJs(mori.last(footnote));
      const labeledNotes = notes.map((note) => Object.assign(
        {},
        note,
        getLabel(mori.hash(note.note))
      ));

      return mori.hashMap(
        // Lets update the notes with labels now
        mori.first(footnote),
        mori.toClj(labeledNotes)
      );
    },
    // Sort It!
    mori.sortBy(
      // Lets grab the weights
      (item) => {
        const keys = mori.first(item);
        const row = mori.count(mori.takeWhile(
          (rowItem) => !mori.isSubset(
            mori.set([
              mori.get(rowItem, mori.set([baseContext, 'Geography']), false) ||
              mori.get(rowItem, mori.first(mori.keys(rowItem))),
            ].map((x) => x.replace('(All)', '').trim())),
            keys
          ),
          mori.get(rows, mori.first(keys))
        ));
        const column = mori.get(
          mori.first(mori.filter(
            // If the 3rd key is a map we need to be very specific,
            // otherwise we don't care
            (columnItem) => (mori.isMap(mori.last(keys) && !isTrendGraph) ?
              mori.equals(
                mori.last(keys),
                mori.hashMap(
                  mori.get(columnItem, 'headerGroup'),
                  mori.get(columnItem, 'characteristic')
                )
              ) :
              mori.last(keys) === mori.get(columnItem, 'headerGroup', false) ||
              mori.last(keys) ===
                mori.get(columnItem, 'characteristic', false) ||
              mori.last(mori.last(mori.last(keys))) ===
                mori.get(columnItem, 'characteristic', false)
            ),
            headers
          )),
          'idx'
        );
        let year = false;
        if (isTrendGraph) {
          // If the timeframe is not "any", and if the last key
          // item is not a set, it's a column title.
          // We do not want to take the year weight into account.
          // Else, compute the year weight with timeframes.  We are adding 1
          // to break JavaScript's 0 = falsey.
          year = mori.first(mori.first(mori.last(keys))) !== 'any' &&
            mori.isMap(mori.last(keys)) ? 1 + mori.count(mori.takeWhile(
              (yearItem) => yearItem !== mori.first(keys),
              mori.rest(mori.reverse(timeframes)) // cut off 'any'
            )) : false;
        }
        return { keys, row, column, year };
      },
      // Lets actually do the sorting
      (
        { row: aRow, column: aColumn, year: aYear = false },
        { row: bRow, column: bColumn, year: bYear = false }
      ) => {
        // If the row weights are equal and there are year weights that are
        // not equal, sort by year weights
        if (aRow === bRow && (aYear && bYear && aYear !== bYear)) {
          return aYear - bYear;
        }

        // Else, check if we should weigh by column or row.
        return (aRow === bRow) ? (aColumn - bColumn) : (aRow - bRow);
      },
      // Convert it to a collection
      mori.reduceKV(
        (acc, key, val) => mori.conj(acc, mori.vector(key, val)),
        mori.set(),
        prepForReduction(footnotes, timeframes)
      )
    )
  );
  // Convert it back to a hashmap
  return mori.reduce(
    (acc, note) => mori.conj(
      acc,
      note,
    ),
    mori.hashMap(),
    labeledFootnotes
  );
}

/**
 * prep footnotes for reduction by replacing 'any' with years
 * @param  {mori.hashMap} footnotes  [current footnotes]
 * @param  {mori.vector} timeframes  [timeframes]
 * @return {mori.hashMap}            [hashMap removed of 'any']
 */
function prepForReduction(footnotes, timeframes) {
  return mori.reduceKV(
    (acc, key, val) => {
      // If timeframes count = 2 it's just a single one, ex: [2017, 'any']
      if (mori.first(key) !== 'any' ||
        (mori.first(key) === 'any' && mori.count(timeframes) === 2)
      ) {
        // If 'any' use the timeframes...
        let actualKey = (mori.first(key) !== 'any') ?
          key :
          mori.set([
            mori.first(timeframes),
            mori.first(mori.rest(key)),
            mori.last(mori.rest(key)),
            mori.first(mori.rest(mori.rest(key))),
          ]);
        // KFF-2902 - if we have a distribution value via: California[VALUE]
        if (mori.count(key) === 4) {
          actualKey = mori.conj(actualKey, mori.last(key));
        }
        // If only one timeframe count is 2. ex: [2016, 'any']
        if (!mori.get(acc, actualKey, false)) {
          return mori.merge(acc, mori.hashMap(actualKey, val));
        }
        // If 'any' we use the 'actual' set key instead
        return mori.merge(
          acc,
          mori.hashMap(
            actualKey,
            mori.into(
              mori.get(acc, actualKey),
              val
            )
          )
        );
      }

      // Key is any and timeframes are > 1
      return mori.merge(
        acc,
        mori.reduce(
          (acc1, time) => {
            const actualKey = mori.set([
              time,
              mori.first(mori.rest(key)),
              mori.last(mori.rest(key)),
            ]);

            // If it exists in acc, we need to combine it
            if (mori.get(acc, actualKey, false)) {
              return mori.merge(
                acc1,
                mori.hashMap(
                  actualKey,
                  mori.into(
                    mori.get(acc, actualKey, false),
                    val
                  )
                )
              );
            }
            // Else we can just toss it in
            return mori.merge(acc1, mori.hashMap(actualKey, val));
          },
          mori.hashMap(),
          mori.filter((time) => time !== 'any', timeframes)
        )
      );
    },
    mori.hashMap(),
    footnotes
  );
}



// WEBPACK FOOTER //
// ../datacenter/app/js/services/getFootnotes/labelFootnotes.js